import {
  Card,
  Menu,
  MenuDivider,
  MenuItem,
  Position,
  Tag,
} from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomPopover from "components/Atoms/CustomPopover"
import NewTag from "components/Atoms/NewTag"
import StatusIcon from "components/Atoms/StatusIcon"
import StyledLink from "components/Atoms/StyledLink"
import AlertDialogue from "components/Common/AlertDialogue"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import NewResourceButton from "components/Organisms/AddResourceButton"
import SerialColumn from "components/Organisms/table/SerialColumn"
import { AppPermissions } from "constants/permissions"
import { useAllFarmersListQuery } from "generated/graphql"
import { DELETE_USER } from "graphql/system-user.gql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import useAuthorization from "hooks/useAuthorization"
import { get } from "lodash-es"
import React, { useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import ReactTable from "react-table"
import FarmerPlotList from "./FarmerPlotList"
import SupplierSelect from "./SupplierSelect"
import UpdateFarmerGroupsCompanies from "./UpdateFarmerGroupsCompanies"
import { Prohibit } from "@phosphor-icons/react"
export default function Farmers() {
  const navigate = useNavigate()
  const [id, setId] = useState(null)
  const canAddPlot = useAuthorization(["vt_add_plot"])
  const [alertVisible, setAlert] = useState(false)

  const COLUMNS = [
    SerialColumn,
    {
      accessor: "actions",
      width: 50,
      Cell({ original }) {
        const [params, setParams] = useSearchParams()

        const canUpdateSupplierGroups = useAuthorization([
          AppPermissions.canUpdateSupplierGroups,
        ])

        const canUpdateSupplierCompanies = useAuthorization([
          AppPermissions.canUpdateSupplierCompanies,
        ])
        return (
          <CustomPopover
            position={Position.BOTTOM_LEFT}
            minimal={true}
            content={
              <Menu>
                <MenuItem
                  icon="plus"
                  disabled={canAddPlot ? false : true}
                  text="Add Plot"
                  onClick={e => handleNewPlot(original)}
                />
                <MenuItem
                  text="Edit Farmer"
                  icon="edit"
                  onClick={e => navigate(original.id)}
                />
                <MenuItem
                  icon="people"
                  text="Update Groups"
                  disabled={canUpdateSupplierGroups ? false : true}
                  onClick={e => {
                    params.set("id", original.id)
                    params.set("action", "update-group")
                    setParams(params)
                  }}
                />
                <MenuItem
                  text="Update Companies"
                  icon="office"
                  disabled={canUpdateSupplierCompanies ? false : true}
                  onClick={e => {
                    params.set("id", original.id)
                    params.set("action", "update-company")
                    setParams(params)
                  }}
                />
                <MenuDivider />
                <MenuItem
                  icon="projects"
                  text="Archive Farmer"
                  onClick={e => handleDelete(original.id)}
                />
              </Menu>
            }
          >
            <CustomButton
              style={{ transform: "rotate(90deg)" }}
              icon="more"
              minimal={true}
            />
          </CustomPopover>
        )
      },
    },
    {
      Header: "Status",
      accessor: "status",
      width: 75,
      Cell: ({ original }) => {
        return (
          <StatusIcon
            values={{
              AV: { intent: "success", text: "AV", tooltip: "Available" },
              NA: { intent: "danger", text: "NA", tooltip: "Not Available" },
              OL: { intent: "warning", text: "OL", tooltip: "On Leave" },
              BS: { intent: "none", text: "BS", tooltip: "Busy" },
            }}
            condition={original.status}
          />
        )
      },
    },
    {
      Header: "Name",
      headerClassName: "px-4",
      accessor: "fullName",
      width: 300,
      Cell: ({ original, value }) => {
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-1">
                <StyledLink
                  className={`vt-highlight ${
                    original?.isBlocked ? "text-red-500" : ""
                  }`}
                  to={`${window.location.pathname}/${original.id}`}
                >
                  <div className="flex items-center gap-2 font-500">
                    {value}
                    {original?.isBlocked && (
                      <Prohibit weight="fill" size={20} color="red" />
                    )}
                  </div>
                </StyledLink>
                {original && original.companyCode && (
                  <p className="mb-0">{original.companyCode}</p>
                )}
              </div>
              <div className="col-span-1">
                <NewTag createdAt={original.createdAt} />
              </div>
            </div>
          </>
        )
      },
    },
    {
      Header: "Supplier Type",
      headerClassName: "px-4",
      accessor: "fullName",
      width: 240,
      Cell: ({ original }) => {
        const groups = get(original, "groups", [])
        const supplierTypes = useMemo(
          () => groups.filter(group => /farmer|trader/i.test(group.name)),
          [groups]
        )

        return (
          <>
            {supplierTypes.map(st => (
              <Tag
                key={st.id}
                className="mr-2"
                intent={/farmer/i.test(st.name) ? "primary" : "warning"}
                minimal
                round
              >
                {st.name}
              </Tag>
            ))}
          </>
        )
      },
    },
    {
      Header: "Mobile Number",
      accessor: "number",
      width: 150,
    },
    {
      Header: "Plots",
      accessor: "plots",
      Cell: row => {
        return <FarmerPlotList plots={row.value} />
      },
    },
  ]

  function handleNewPlot(farmer) {
    const { id, fullName, username } = farmer
    navigate(`/d/registration/plot/new`, { state: { id, fullName, username } })
  }
  function handleDelete(id) {
    setId(id)
    setAlert(true)
  }

  const [{ page, group, search }, setFilter] = useSearchFilter()
  const { data, loading } = useAllFarmersListQuery({
    variables: {
      group: group ?? "Farmer",
      query: search ?? "",
      first: 13,
      skip: page * 13,
    },
  })
  return (
    <Card className="w-full p-0">
      <ReactTableHeader
        icon="shop"
        title="Supplier"
        value={search}
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
      >
        <SupplierSelect
          onChange={(_, item) => {
            setFilter(prev => ({
              ...prev,
              group: item.name,
            }))
          }}
          value={group ?? "Farmer"}
        />
        <TablePagination
          className="mr-6"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />
        <NewResourceButton module="supplier" />
      </ReactTableHeader>
      <ReactTable
        loading={loading}
        manual
        sortable={false}
        showPagination={false}
        className="mt-0 text-center vt-master-table"
        resizable={true}
        getTrProps={(_, rowInfo) => {
          if (rowInfo && rowInfo.original) {
            return rowInfo.original?.isBlocked
              ? {
                  className: "bg-red-50",
                }
              : {}
          }
          return {}
        }}
        data={get(data, "usersByGroup", [])}
        columns={COLUMNS}
      />

      <AlertDialogue
        alertFor="Farmer"
        isOpen={alertVisible}
        onClose={value => setAlert(value)}
        mutation={DELETE_USER}
        variables={{
          id,
        }}
      />
      <UpdateFarmerGroupsCompanies />
    </Card>
  )
}
